import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Plane, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import LoginInstallPrompt from "@/components/LoginInstallPrompt";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(user.landing_page || "/clock");
  }, [user, navigate]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(username, password);
      toast.success(`Welcome, signed in as ${username}`);
      navigate(u?.landing_page || "/clock");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-summit-canvas">
      {/* Left: hero panel — background is the Summit Air corporate flight video
          (sourced from flysummitair.com, compressed to a web-friendly 1280p
          25-second loop). A high-res still frame is used as the poster so the
          hero looks great even before the video buffers. */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-[#0B1220] text-white">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/media/summit-hero.jpg"
          data-testid="login-hero-video"
        >
          <source src="/media/summit-hero-web.webm" type="video/webm" />
          <source src="/media/summit-hero-web.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1220]/85 via-[#0B1220]/55 to-[#0B1220]/95" />
        <div className="relative z-10 p-12 lg:p-16 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <img src="/media/sa-logo.png" alt="Summit Air" className="w-10 h-10 object-contain bg-white rounded-md" data-testid="login-logo" />
            <div>
              <div className="font-display font-semibold text-lg leading-tight">SA</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">System</div>
            </div>
          </div>

          <div className="max-w-xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Precision in every corner.
            </h1>
          </div>

          {/* Right-most column intentionally blank — the previous 3-stat grid
              (tail-number count, fleet count, pay-period length) has been
              removed per the crew's request to keep operational details off
              the public sign-in surface. */}
          <div />
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/media/sa-logo.png" alt="Summit Air" className="w-9 h-9 object-contain bg-white rounded-md" data-testid="login-logo-mobile" />
            <div>
              <div className="font-display font-semibold text-base">SA</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">System</div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-gray-500 mt-1">Enter your SA credentials.</p>

          <form className="mt-6 space-y-4" onSubmit={submit} data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-widest text-gray-500">Username</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="mt-1.5"
                data-testid="login-username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-gray-500">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </form>
        </div>
      </div>
      {/* PWA install nudge — opens automatically when running in browser
          (not standalone). Persistent per user's Iter-50 choice. */}
      <LoginInstallPrompt />
    </div>
  );
}
